import React, {useCallback, useState, useRef, useEffect} from "react";
import {Rocket, Play, TextCursorInput} from "lucide-react";
import {Button} from "@/components/ui/Button";
import Checklist from "./Checklist";
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {useSave} from "@/contexts/SaveContext";
import {useSubflows} from "@/contexts/SubflowsContext";
import AutoSave from "./AutoSave";
import {FlowMode, FlowNodeSetting, SavingState} from "@/types/node";
import {Separator} from "@/components/ui/separator";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {cn} from "@/lib/utils";
import {useDrawer} from "@/contexts/DrawerContext";
import {SwitchModeModal} from "./Modals/SwitchModeModal";
import {DeployModal} from "./Modals/DeployModal";
import {getSettingValueByKey} from "@/utils/index";
import {
  detachActionFromAssistant,
  getAssistantActions,
  attachActionToAssistantBatch,
} from "@/api/services/queries";
import {AssistantActionAttachmentTypeModel} from "@/api/models";
import {BaseAction} from "@/types/api";
import {Action} from "./Settings";

enum FlowStatusEnum {
  DRAFT = "draft",
  PUBLISHED = "published",
}

const MODAL_CLOSE_DELAY = 300;

const FlowStatus: React.FC<{status: string}> = ({status}) => {
  if (status === FlowStatusEnum.DRAFT) {
    return (
      <div className="text-xs px-1.5 py-0.5 rounded-md border border-divider text-label">
        Draft
      </div>
    );
  } else if (status === FlowStatusEnum.PUBLISHED) {
    return (
      <div className="text-xs px-1.5 py-0.5 rounded-md bg-green bg-opacity-12 text-green-dark">
        Published
      </div>
    );
  }
};

const Topbar: React.FC = () => {
  const {flowMode, setFlowMode, handleSave, savingState, setSavingState} =
    useSave();
  const {mainFlow, subflows, selectedSubflow, updateSubflow} = useSubflows();
  const disabled = savingState === SavingState.LOADING;

  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showSwitchModeModal, setShowSwitchModeModal] = useState(false);
  const {openDrawer} = useDrawer();

  // Keep refs updated with latest values for publish operation
  const mainFlowRef = useRef(mainFlow);
  const subflowsRef = useRef(subflows);
  const selectedSubflowRef = useRef(selectedSubflow);

  useEffect(() => {
    mainFlowRef.current = mainFlow;
  }, [mainFlow]);

  useEffect(() => {
    subflowsRef.current = subflows;
  }, [subflows]);

  useEffect(() => {
    selectedSubflowRef.current = selectedSubflow;
  }, [selectedSubflow]);

  const isLiveMode = flowMode === FlowMode.LIVE;

  const deduplicateActionsByActionId = (actions: Action[]): BaseAction[] => {
    return Array.from(
      actions
        .filter(action => action?.action_id)
        .reduce((acc, action) => {
          if (action?.action_id && !acc.has(action.action_id)) {
            acc.set(action.action_id, action as BaseAction);
          }
          return acc;
        }, new Map<string, BaseAction>())
        .values(),
    );
  };

  const handlePublish = async () => {
    setSavingState(SavingState.SAVING);

    const [liveCustomActions, liveBookingActions, liveTransferActions] =
      await Promise.all([
        getAssistantActions(FlowBuilderSettings.modelId, {
          action_type: "custom",
          attachment_type: "during",
        }),
        getAssistantActions(FlowBuilderSettings.modelId, {
          action_type: "booking",
          attachment_type: "during",
        }),
        getAssistantActions(FlowBuilderSettings.modelId, {
          action_type: "transfer",
          attachment_type: "during",
        }),
      ]);

    // Use refs to get the latest values
    const latestMainFlow = mainFlowRef.current;
    const latestSubflows = subflowsRef.current;

    // Collect all test nodes from main flow and all subflows
    const allTestNodes = [
      ...latestMainFlow.flowData.test.nodes,
      ...latestSubflows.flatMap(subflow => subflow.flowData.test.nodes),
    ];

    // Remove duplicate actions by action_id for both testFlowActions and liveFlowActions
    const testFlowActions = deduplicateActionsByActionId(
      allTestNodes.map(
        node =>
          getSettingValueByKey(
            node.data.settings as FlowNodeSetting[],
            "action",
          ) as Action,
      ),
    );

    const allLiveActions = [
      ...liveCustomActions.items,
      ...liveBookingActions.items,
      ...liveTransferActions.items,
    ] as Action[];

    const liveFlowActions = deduplicateActionsByActionId(allLiveActions);

    try {
      if (liveFlowActions.length > 0) {
        liveFlowActions.forEach(async action => {
          await detachActionFromAssistant(
            FlowBuilderSettings.modelId,
            action.slug,
            {
              attachment_type: AssistantActionAttachmentTypeModel.DURING,
            },
          );
        });
      }
      if (testFlowActions.length > 0) {
        await attachActionToAssistantBatch(FlowBuilderSettings.modelId, {
          items: testFlowActions.map(action => ({
            slug: action.slug,
            attachment_type: AssistantActionAttachmentTypeModel.DURING,
          })),
        });
      }
    } catch (err) {
      console.error(err);
      setSavingState(SavingState.ERROR_ACTIONS);
      return;
    }

    // Copy test data to live for ALL subflows and main flow
    // Update main flow: copy test to live
    updateSubflow("main", {
      flowData: {
        ...latestMainFlow.flowData,
        live: {
          version: latestMainFlow.flowData.test.version,
          nodes: latestMainFlow.flowData.test.nodes,
          edges: latestMainFlow.flowData.test.edges,
        },
      },
    });

    // Update all subflows: copy test to live
    latestSubflows.forEach(subflow => {
      updateSubflow(subflow.id, {
        flowData: {
          ...subflow.flowData,
          live: {
            version: subflow.flowData.test.version,
            nodes: subflow.flowData.test.nodes,
            edges: subflow.flowData.test.edges,
          },
        },
      });
    });

    // Wait for state updates to propagate before saving
    setTimeout(() => {
      handleSave(() => {
        setShowDeployModal(false);
      }, true);
    }, 0);
  };

  const handleSwitchMode = useCallback(() => {
    handleSave(
      () => {
        setShowSwitchModeModal(false);
        setTimeout(() => {
          FlowBuilderSettings.onModeChange &&
            FlowBuilderSettings.onModeChange();
        }, MODAL_CLOSE_DELAY);
      },
      false,
      true,
    );
  }, [handleSave, setShowSwitchModeModal]);

  return (
    <div
      className={cn(
        "flex justify-between items-center px-5 py-2.5 border-b bg-background",
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      <div className="flex gap-3 flex-1">
        <div className="flex">
          <h2 className=" flex flex-row items-center gap-2 font-medium overflow-hidden text-ellipsis whitespace-nowrap max-w-[280px]">
            Flow v2
            {/* <FlowStatus status={"draft"} /> */}
          </h2>
        </div>
        {FlowBuilderSettings.onModeChange && (
          <Button
            variant="outline"
            className="[&_svg]:size-5"
            onClick={() => {
              setShowSwitchModeModal(true);
            }}
          >
            <TextCursorInput className="text-primary-accent" />
            Switch to Prompt
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        <div className="flex gap-1">{!isLiveMode && <Checklist />}</div>
        {!isLiveMode && (
          <Button
            className="[&_svg]:size-5"
            onClick={() => setShowDeployModal(true)}
            disabled={savingState !== SavingState.SAVED}
          >
            <Rocket />
            Publish
          </Button>
        )}
        <Tabs
          defaultValue={flowMode}
          onValueChange={value => {
            setFlowMode(value as FlowMode);
          }}
        >
          <TabsList>
            <TabsTrigger
              value={FlowMode.LIVE}
              className="rounded cursor-pointer"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-green-light border border-green mr-2" />
              Live
            </TabsTrigger>
            <TabsTrigger
              value={FlowMode.TEST}
              className="rounded cursor-pointer"
            >
              Edit Mode
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Separator orientation="vertical" className="h-auto -my-2.5" />
        <AutoSave disabled={isLiveMode} />
      </div>
      {/* MODALS */}
      {/* TODO: create a modal context and move to modal context in new next update */}
      {showDeployModal && (
        <DeployModal
          onPublish={handlePublish}
          showModal={showDeployModal}
          setShowModal={setShowDeployModal}
          isPublishing={savingState === SavingState.SAVING}
        />
      )}
      {showSwitchModeModal && (
        <SwitchModeModal
          onSwitchMode={handleSwitchMode}
          showModal={showSwitchModeModal}
          setShowModal={setShowSwitchModeModal}
          loading={savingState === SavingState.SAVING}
        />
      )}
    </div>
  );
};

export default Topbar;
